import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  Heading,
  Link,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  Textarea,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { aiPromptStreamApi } from 'api/aiPromptApi';
import Card from 'components/card/Card';
import { useRef, useState } from 'react';

const isReasoningModel = (value) => /^o\d/.test(value);

const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'];

const MODELS = [
  { value: 'gpt-5', label: 'GPT-5', webSearch: true },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini', webSearch: true },
  { value: 'gpt-4o', label: 'GPT-4o', webSearch: true },
  { value: 'gpt-4.1-nano', label: 'GPT-4.1 Nano', webSearch: true },
  { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini', webSearch: true },
  { value: 'gpt-4.1', label: 'GPT-4.1', webSearch: true },
  { value: 'o4-mini', label: 'o4 Mini (reasoning)', webSearch: false },
  { value: 'o3-mini', label: 'o3 Mini (reasoning)', webSearch: false },
  { value: 'o3', label: 'o3 (reasoning)', webSearch: false },
  { value: 'o1', label: 'o1 (reasoning)', webSearch: false },
  // Claude Opus 5 / Sonnet 5 reject temperature/top_p entirely (HTTP 400) — use
  // effort instead. Haiku 4.5 still accepts sampling params, no effort control.
  { value: 'claude-opus-5', label: 'Claude Opus 5', webSearch: true, samplingDisabled: true, supportsEffort: true },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5', webSearch: true, samplingDisabled: true, supportsEffort: true },
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', webSearch: true, samplingDisabled: false, supportsEffort: false },
];

function tryFormatJSON(text) {
  try {
    const parsed = JSON.parse(text);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return null;
  }
}

export default function AiPrompt() {
  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState(MODELS[0].value);
  const [webSearch, setWebSearch] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [temperature, setTemperature] = useState(1.0);
  const [topP, setTopP] = useState(1.0);
  const [maxOutputTokens, setMaxOutputTokens] = useState('');
  const [effort, setEffort] = useState('high');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [result, setResult] = useState(null);
  const abortRef = useRef(null);

  const selectedModel = MODELS.find((m) => m.value === model) ?? MODELS[0];
  const webSearchDisabled = !selectedModel.webSearch;
  const samplingDisabled = isReasoningModel(model) || !!selectedModel.samplingDisabled;
  const topPDisabled = samplingDisabled || temperature !== 1.0;
  const temperatureDisabled = samplingDisabled || topP !== 1.0;
  const effortDisabled = !selectedModel.supportsEffort;

  const onModelChange = (e) => {
    const next = e.target.value;
    const nextModel = MODELS.find((x) => x.value === next);
    setModel(next);
    if (!nextModel?.webSearch) setWebSearch(false);
    // Opus 5 / Sonnet 5 reject temperature/top_p outright — reset any value
    // carried over from a model that does support sampling.
    if (nextModel?.samplingDisabled) {
      setTemperature(1.0);
      setTopP(1.0);
    }
  };

  const onTemperatureChange = (val) => {
    setTemperature(val);
    if (val !== 1.0) setTopP(1.0); // mutual exclusion
  };

  const onTopPChange = (val) => {
    setTopP(val);
    if (val !== 1.0) setTemperature(1.0); // mutual exclusion
  };

  const onSubmit = () => {
    const trimmed = prompt.trim();
    if (!trimmed) {
      toast({ title: 'Enter a prompt', status: 'warning' });
      return;
    }
    abortRef.current = new AbortController();
    setLoading(true);
    setStreamText('');
    setResult(null);

    aiPromptStreamApi({
      prompt: trimmed,
      model,
      webSearch: webSearch && !webSearchDisabled,
      systemPrompt: systemPrompt.trim() || undefined,
      temperature: samplingDisabled ? undefined : temperature,
      topP: samplingDisabled ? undefined : topP,
      maxOutputTokens: maxOutputTokens ? parseInt(maxOutputTokens, 10) : undefined,
      effort: effortDisabled ? undefined : effort,
      signal: abortRef.current.signal,
      onDelta: (text) => setStreamText((prev) => prev + text),
      onDone: (data) => {
        setResult(data);
        setLoading(false);
      },
      onError: (msg) => {
        setLoading(false);
        toast({ title: 'Error', description: msg || 'Request failed', status: 'error' });
      },
    });
  };

  const onCancel = () => {
    abortRef.current?.abort();
    setLoading(false);
  };

  // While streaming show accumulated text; after done use result.text (same content)
  const displayText = result?.text ?? streamText;
  const isDone = !!result;
  const jsonFormatted = isDone && displayText ? tryFormatJSON(displayText) : null;
  const citations = result?.citations ?? [];

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card>
        <Heading variant="sectionHeader" mb={6}>
          AI Prompt
        </Heading>

        <Flex direction="column" gap={4} maxW="800px">
          <Textarea
            placeholder="Enter your prompt here..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={6}
            resize="vertical"
          />

          <Flex align="center" gap={4} flexWrap="wrap">
            <Select value={model} onChange={onModelChange} maxW="220px" size="sm">
              {MODELS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>

            <Tooltip
              label="Web search is not available for reasoning models"
              isDisabled={!webSearchDisabled}
            >
              <Checkbox
                isChecked={webSearch && !webSearchDisabled}
                onChange={(e) => setWebSearch(e.target.checked)}
                isDisabled={webSearchDisabled}
                size="sm"
              >
                Web search
              </Checkbox>
            </Tooltip>

            <Button variant="brand" onClick={onSubmit} isDisabled={loading}>
              Submit
            </Button>

            {loading && (
              <Button variant="outline" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </Flex>

          <Box>
            <Button
              variant="link"
              size="sm"
              color="secondaryGray.600"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? '▾ Hide advanced options' : '▸ Advanced options'}
            </Button>
            <Collapse in={showAdvanced} animateOpacity>
              <Flex direction="column" gap={4} mt={3} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">

                {/* System prompt */}
                <Box>
                  <Text fontSize="sm" fontWeight={600} mb={1}>
                    System prompt
                  </Text>
                  <Text fontSize="xs" color="secondaryGray.500" mb={2}>
                    Instructions prepended before your prompt — set the model's role, tone, or output format.
                  </Text>
                  <Textarea
                    placeholder='e.g. "You are a concise financial analyst. Always respond in bullet points."'
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    rows={3}
                    resize="vertical"
                    fontSize="sm"
                  />
                </Box>

                {/* Temperature */}
                <Box>
                  <Flex align="center" justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight={600}>
                      Temperature
                      {samplingDisabled && (
                        <Text as="span" fontWeight={400} color="secondaryGray.500" ml={2} fontSize="xs">
                          (not available for this model)
                        </Text>
                      )}
                      {!samplingDisabled && topP !== 1.0 && (
                        <Text as="span" fontWeight={400} color="secondaryGray.500" ml={2} fontSize="xs">
                          (reset Top P to use this)
                        </Text>
                      )}
                    </Text>
                    <Badge colorScheme={temperature !== 1.0 ? 'brand' : 'gray'} fontSize="sm" minW="36px" textAlign="center">
                      {temperature.toFixed(1)}
                    </Badge>
                  </Flex>
                  <Text fontSize="xs" color="secondaryGray.500" mb={2}>
                    0 = deterministic &amp; factual · 1 = balanced (default) · 2 = creative &amp; varied
                  </Text>
                  <Slider
                    min={0} max={2} step={0.1}
                    value={temperature}
                    onChange={onTemperatureChange}
                    isDisabled={temperatureDisabled}
                  >
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                  </Slider>
                </Box>

                {/* Top P */}
                <Box>
                  <Flex align="center" justify="space-between" mb={1}>
                    <Text fontSize="sm" fontWeight={600}>
                      Top P
                      {samplingDisabled && (
                        <Text as="span" fontWeight={400} color="secondaryGray.500" ml={2} fontSize="xs">
                          (not available for this model)
                        </Text>
                      )}
                      {!samplingDisabled && temperature !== 1.0 && (
                        <Text as="span" fontWeight={400} color="secondaryGray.500" ml={2} fontSize="xs">
                          (reset Temperature to use this)
                        </Text>
                      )}
                    </Text>
                    <Badge colorScheme={topP !== 1.0 ? 'brand' : 'gray'} fontSize="sm" minW="36px" textAlign="center">
                      {topP.toFixed(2)}
                    </Badge>
                  </Flex>
                  <Text fontSize="xs" color="secondaryGray.500" mb={2}>
                    Nucleus sampling — considers only the top P probability mass. 0.1 = very focused, 1.0 = default (all tokens).
                  </Text>
                  <Slider
                    min={0.01} max={1} step={0.01}
                    value={topP}
                    onChange={onTopPChange}
                    isDisabled={topPDisabled}
                  >
                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                    <SliderThumb />
                  </Slider>
                </Box>

                {/* Max output tokens */}
                <Box>
                  <Text fontSize="sm" fontWeight={600} mb={1}>Max output tokens</Text>
                  <Text fontSize="xs" color="secondaryGray.500" mb={2}>
                    Cap response length. Leave blank for model default.
                  </Text>
                  <NumberInput
                    min={1} max={100000}
                    value={maxOutputTokens}
                    onChange={(val) => setMaxOutputTokens(val)}
                    maxW="180px"
                    size="sm"
                  >
                    <NumberInputField placeholder="e.g. 2000" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>

                {/* Effort (Claude Opus 5 / Sonnet 5 only) */}
                <Box>
                  <Text fontSize="sm" fontWeight={600} mb={1}>
                    Effort
                    {effortDisabled && (
                      <Text as="span" fontWeight={400} color="secondaryGray.500" ml={2} fontSize="xs">
                        (only available for Claude Opus 5 / Sonnet 5)
                      </Text>
                    )}
                  </Text>
                  <Text fontSize="xs" color="secondaryGray.500" mb={2}>
                    Controls Claude's thinking depth. Low = fast &amp; cheap · High = default · Max = most thorough.
                  </Text>
                  <Select
                    value={effort}
                    onChange={(e) => setEffort(e.target.value)}
                    isDisabled={effortDisabled}
                    maxW="180px"
                    size="sm"
                  >
                    {EFFORT_LEVELS.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </Select>
                </Box>

              </Flex>
            </Collapse>
          </Box>

          {(streamText || result) && (
            <Box mt={4}>
              <Flex align="center" gap={2} mb={2}>
                <Text fontWeight={600} fontSize="sm" color="secondaryGray.600">
                  Result
                </Text>
                {loading && (
                  <Badge colorScheme="yellow" fontSize="xs">
                    generating…
                  </Badge>
                )}
                {isDone && result.totalTokens > 0 && (
                  <Badge colorScheme="gray" fontSize="xs">
                    {result.totalTokens} tokens
                  </Badge>
                )}
                {isDone && citations.length > 0 && (
                  <Badge colorScheme="blue" fontSize="xs">
                    {citations.length} source{citations.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </Flex>

              <Box
                as="pre"
                p={4}
                bg="gray.50"
                borderRadius="md"
                fontSize="sm"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                border="1px solid"
                borderColor={loading ? 'brand.200' : 'gray.200'}
                maxH="500px"
                overflowY="auto"
              >
                {jsonFormatted ?? displayText}
                {loading && <Box as="span" opacity={0.5}>▌</Box>}
              </Box>

              {isDone && citations.length > 0 && (
                <Box mt={3}>
                  <Text fontWeight={600} fontSize="sm" color="secondaryGray.600" mb={1}>
                    Sources
                  </Text>
                  <Flex direction="column" gap={1}>
                    {citations.map((c, i) => (
                      <Flex key={i} align="baseline" gap={2} fontSize="sm">
                        <Text color="secondaryGray.500" flexShrink={0}>
                          [{i + 1}]
                        </Text>
                        <Link href={c.url} isExternal color="brand.500" noOfLines={1}>
                          {c.title || c.url}
                        </Link>
                      </Flex>
                    ))}
                  </Flex>
                </Box>
              )}
            </Box>
          )}
        </Flex>
      </Card>
    </Box>
  );
}
